import React, { useState } from 'react';
import { FileText, Download, FileSpreadsheet, AlertTriangle, PieChart, FolderOpen, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const reportTypes = [
  {
    id: 'bilan-annuel',
    name: 'Bilan annuel',
    description: 'Synthèse financière complète de l\'exercice',
    icon: PieChart,
    color: 'bg-blue-500',
    endpoint: (exerciceId) => `/api/reports/bilan-annuel/${exerciceId}`
  },
  {
    id: 'comparatif-budget',
    name: 'Comparatif budgétaire',
    description: 'Budget prévisionnel vs dépenses réalisées',
    icon: FileSpreadsheet,
    color: 'bg-green-500',
    endpoint: (exerciceId) => `/api/reports/comparatif-budget/${exerciceId}`
  },
  {
    id: 'impayes',
    name: 'État des impayés',
    description: 'Liste des coproprietaires avec solde débiteur',
    icon: AlertTriangle,
    color: 'bg-orange-500',
    endpoint: (exerciceId, buildingId) => `/api/reports/impayes/${buildingId}`
  },
  {
    id: 'dossier-ag',
    name: 'Dossier financier AG',
    description: 'Package complet pour l\'Assemblée Générale',
    icon: FolderOpen,
    color: 'bg-purple-500',
    endpoint: (exerciceId) => `/api/reports/dossier-ag/${exerciceId}`
  }
];

export default function ReportsSection({ buildingId, exerciceId, token }) {
  const [downloading, setDownloading] = useState(null);

  const handleDownload = async (report) => {
    if (!exerciceId && report.id !== 'impayes') {
      toast.error('Veuillez sélectionner un exercice');
      return;
    }

    setDownloading(report.id);

    try {
      const endpoint = report.endpoint(exerciceId, buildingId);
      const response = await fetch(`${API_URL}${endpoint}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = `${report.name.replace(/ /g, '_')}.pdf`;
        
        if (contentDisposition) {
          const match = contentDisposition.match(/filename=(.+)/);
          if (match) {
            filename = match[1].replace(/"/g, '');
          }
        }

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        toast.success(`${report.name} téléchargé`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la génération');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Rapports financiers</h2>
        <p className="text-sm text-gray-500">
          Générez les documents officiels pour l'Assemblée Générale
        </p>
      </div>

      {/* Reports Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {reportTypes.map((report) => {
          const Icon = report.icon;
          const isDownloading = downloading === report.id;

          return (
            <div
              key={report.id}
              className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-xl ${report.color}`}>
                  <Icon className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">{report.name}</h3>
                  <p className="text-sm text-gray-500 mt-1">{report.description}</p>
                </div>
              </div>
              
              <button
                onClick={() => handleDownload(report)}
                disabled={isDownloading}
                className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors"
                data-testid={`download-${report.id}`}
              >
                {isDownloading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Génération...
                  </>
                ) : (
                  <>
                    <Download className="h-5 w-5" />
                    Télécharger PDF
                  </>
                )}
              </button>
            </div>
          );
        })}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <FileText className="h-5 w-5 text-blue-500 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-800">Documents pour l'AG</h4>
            <p className="text-sm text-blue-600 mt-1">
              Le <strong>Dossier financier AG</strong> contient tous les documents requis par la loi 
              pour présenter les comptes en Assemblée Générale : bilan, comparatif budgétaire, 
              état des impayés et situation des comptes bancaires.
            </p>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      {!exerciceId && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
          <p className="text-sm text-orange-700">
            <strong>Note :</strong> Sélectionnez un exercice comptable pour générer les rapports 
            Bilan annuel, Comparatif budgétaire et Dossier AG.
          </p>
        </div>
      )}
    </div>
  );
}
